import type { TFunction } from "i18next";

import {
  BuildingTypeEnum,
  Construction,
  HouseOrApartmentTypeEnum,
  ObjectTypeEnum,
  OwnershipEnum,
} from "@/types/refineRealEstate";

import { snakeToCamelCase } from "./formatStringHelpers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericTranslator = TFunction<any>;

export const getObjectTypeOptions = (
  t: GenericTranslator,
): {
  value: ObjectTypeEnum;
  label: string;
  disabled?: boolean;
}[] => {
  return [
    {
      value: ObjectTypeEnum.house,
      label: t("objectCategory.values.house"),
    },
    {
      value: ObjectTypeEnum.apartment,
      label: t("objectCategory.values.apartment"),
    },
    {
      value: ObjectTypeEnum.multiFamilyHouse,
      label: t("objectCategory.values.multiFamilyHouse"),
    },
    {
      value: ObjectTypeEnum.mixedUseProperty,
      label: t("objectCategory.values.mixedUseProperty"),
    },
    {
      value: ObjectTypeEnum.plotOfLand,
      label: t("objectCategory.values.plotOfLand"),
    },
  ];
};

export const getHouseBuildingTypeOptions = (
  t: GenericTranslator,
): {
  value: BuildingTypeEnum;
  label: string;
}[] => [
  {
    value: BuildingTypeEnum.singleFamilyHouse,
    label: t("objectCategory.values.singleFamilyHouse"),
  },
  {
    value: BuildingTypeEnum.singleFamilyHouseWithGrannyApartment,
    label: t("objectCategory.values.singleFamilyHouseWithGrannyApartment"),
  },
  {
    value: BuildingTypeEnum.twoFamilyHouse,
    label: t("objectCategory.values.twoFamilyHouse"),
  },
];

export const getObjectTypeLabelWIthBuildingType = (
  objectType: ObjectTypeEnum,
  t: GenericTranslator,
  buildingType?: BuildingTypeEnum | null,
): string => {
  if (objectType !== ObjectTypeEnum.house) {
    return t(`objectCategory.values.${snakeToCamelCase(objectType)}` as ObjectCategoryTranslationKeys);
  }
  if (buildingType) {
    return t(`objectCategory.values.${snakeToCamelCase(buildingType)}` as ObjectCategoryTranslationKeys);
  }
  return t(`objectCategory.values.singleFamilyHouse`);
};

export type ObjectCategoryTranslationKeys =
  | "objectCategory.values.singleFamilyHouse"
  | "objectCategory.values.singleFamilyHouseWithGrannyApartment"
  | "objectCategory.values.twoFamilyHouse"
  | "objectCategory.values.apartment"
  | "objectCategory.values.multiFamilyHouse"
  | "objectCategory.values.mixedUseProperty"
  | "objectCategory.values.plot";

export type OwnershipTranslationKeys =
  | "ownership.values.fullOwnership"
  | "ownership.values.leasehold"
  | "ownership.values.apartmentOwnership"
  | "ownership.values.leaseholdApartmentOwnership";

export const ownershipTranslationMap: Record<OwnershipEnum, OwnershipTranslationKeys> = {
  [OwnershipEnum.fullOwnership]: "ownership.values.fullOwnership",
  [OwnershipEnum.leasehold]: "ownership.values.leasehold",
  [OwnershipEnum.apartmentOwnership]: "ownership.values.apartmentOwnership",
  [OwnershipEnum.leaseholdApartmentOwnership]: "ownership.values.leaseholdApartmentOwnership",
};

export const getOwnershipOptions = (
  t: GenericTranslator,
): {
  value: OwnershipEnum;
  label: string;
}[] => {
  return Object.entries(OwnershipEnum).map(([key, value]) => ({
    value,
    label: t(`ownership.values.${key}` as OwnershipTranslationKeys),
  }));
};

export const optionsByObjectType = {
  [ObjectTypeEnum.house]: Object.values(OwnershipEnum) as OwnershipEnum[],
  [ObjectTypeEnum.apartment]: [
    OwnershipEnum.apartmentOwnership,
    OwnershipEnum.leaseholdApartmentOwnership,
  ] as OwnershipEnum[],
  [ObjectTypeEnum.multiFamilyHouse]: [OwnershipEnum.leasehold, OwnershipEnum.fullOwnership] as OwnershipEnum[],
  [ObjectTypeEnum.mixedUseProperty]: [OwnershipEnum.leasehold, OwnershipEnum.fullOwnership] as OwnershipEnum[],
  [ObjectTypeEnum.plotOfLand]: [OwnershipEnum.fullOwnership] as OwnershipEnum[],
};

export const getObjectTypeTranslation = (objectType: ObjectTypeEnum | undefined, t: GenericTranslator): string => {
  switch (objectType) {
    case ObjectTypeEnum.house:
      return t("objectCategory.values.house");
    case ObjectTypeEnum.apartment:
      return t("objectCategory.values.apartment");
    case ObjectTypeEnum.multiFamilyHouse:
      return t("objectCategory.values.multiFamilyHouse");
    case ObjectTypeEnum.mixedUseProperty:
      return t("objectCategory.values.mixedUseProperty");
    case ObjectTypeEnum.plotOfLand:
      return t("objectCategory.values.plot");
    default:
      return "";
  }
};

export const getHouseOrApartmentTypeTranslation = (
  houseOrApartmentType: HouseOrApartmentTypeEnum | undefined,
  t: GenericTranslator,
): string => {
  switch (houseOrApartmentType) {
    case HouseOrApartmentTypeEnum.detachedHouse:
      return t("houseOrApartmentType.values.detachedHouse");
    case HouseOrApartmentTypeEnum.semiDetachedHouse:
      return t("houseOrApartmentType.values.semidetachedHouse");
    case HouseOrApartmentTypeEnum.endTerraceHouse:
      return t("houseOrApartmentType.values.endTerraceHouse");
    case HouseOrApartmentTypeEnum.midTerracedHouse:
      return t("houseOrApartmentType.values.midTerracedHouse");
    default:
      return "";
  }
};

export const getConstructionTranslation = (construction: Construction | undefined, t: GenericTranslator): string => {
  switch (construction) {
    case Construction.massive:
      return t("construction.values.solid");
    case Construction.prefabHouseSolid:
      return t("construction.values.prefabHouseSolid");
    case Construction.prefabHouseWood:
      return t("construction.values.prefabHouseWood");
    default:
      return "";
  }
};

export const getSourceTranslation = (sourceType: string | undefined | null, t: TFunction<"OneClickPage">): string => {
  if (!sourceType) return "";

  const sourceTranslations: Record<string, string> = {
    INFAS: t("dataDetails.source.infas"),
    OMA: t("dataDetails.source.oma"),
    VDE: t("dataDetails.source.vde"),
    USER_INPUT: t("dataDetails.source.userInput"),
    EA48: t("dataDetails.source.ea48"),
    LOD: t("dataDetails.source.lod"),
    SNAVM: t("dataDetails.source.snavm"),
    SNDB: t("dataDetails.source.sndb"),
    DEFAULT: t("dataDetails.source.default"),
  };
  return sourceTranslations[sourceType] || "";
};

export const formatFloorNumber = (floorNumber: number, t: GenericTranslator): string => {
  if (floorNumber === -1) {
    return t("floorNumber.label.ug");
  } else if (floorNumber === 0) {
    return t("floorNumber.label.eg");
  } else if (floorNumber > 0) {
    return `${floorNumber}.${t("floorNumber.label.og")}`;
  } else {
    return `${Math.abs(floorNumber)}.${t("floorNumber.label.ug")}`;
  }
};
